import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Card, Field } from '@/components/ui/primitives';
import { ApiRequestError } from '@/lib/api';
import { useLogin } from '@/lib/queries';

const searchSchema = z.object({ demo: z.boolean().optional() });

export const Route = createFileRoute('/login')({
  component: Login,
  validateSearch: searchSchema,
});

/** Published in the README; the account is read-only server-side. */
const DEMO_CREDENTIALS = { identifier: 'demo', password: 'cambio-demo' };

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { demo } = Route.useSearch();
  const login = useLogin();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (credentials: { identifier: string; password: string }) => {
    setError(null);
    try {
      await login.mutateAsync(credentials);
      await navigate({ to: '/app/ledger' });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t('errors.generic'));
    }
  };

  // Arriving from the landing page's "view demo" signs straight in, so trying
  // the app is one click rather than a form with pre-filled boxes.
  useEffect(() => {
    if (demo) void submit(DEMO_CREDENTIALS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo]);

  return (
    <div className="landing-shell flex min-h-dvh flex-col items-center justify-center bg-[var(--bg)] px-5">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-2.5 text-[19px] font-semibold tracking-tight"
        >
          <span className="brand-mark flex size-9 items-center justify-center rounded-[11px] bg-[var(--accent)] text-white">
            <ArrowLeftRight size={18} strokeWidth={2.35} />
          </span>
          <span>{t('common.appNameFull')}</span>
        </Link>

        <Card>
          <h1 className="text-[22px] font-bold">{t('auth.signIn')}</h1>

          <form
            className="mt-5 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submit({ identifier, password });
            }}
          >
            <Field
              label={t('auth.identifier')}
              autoComplete="username"
              autoCapitalize="none"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
            <Field
              label={t('auth.password')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              {...(error ? { error } : {})}
            />
            <Button type="submit" variant="primary" size="lg" disabled={login.isPending}>
              {login.isPending ? `${t('common.loading')}…` : t('auth.signIn')}
            </Button>
          </form>

          <div className="mt-5 border-t border-separator pt-4">
            <p className="text-[13px] text-secondary">{t('auth.demoHint')}</p>
            <Button
              variant="secondary"
              className="mt-2 w-full"
              disabled={login.isPending}
              onClick={() => void submit(DEMO_CREDENTIALS)}
            >
              {t('auth.tryDemo')}
            </Button>
          </div>
        </Card>

        <p className="pt-5 text-center text-[15px] text-secondary">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="font-medium text-[var(--accent)]">
            {t('auth.signUp')}
          </Link>
        </p>
      </div>
    </div>
  );
}
